import { useCallback, useEffect, useMemo, useState } from "react";
import type { CSSProperties } from "react";
import { CustomSelect } from "./CustomSelect";
import type { CustomSelectOption } from "./CustomSelect";
import { ApiError, whatsapp } from "../lib/api";
import type {
  EventMappingOut,
  WhatsappAdminStatusOut,
  WhatsappMessageItem,
  WhatsappMetricsOut,
  WhatsappTemplateItem,
} from "../lib/api";

const REFRESH_MS = 30_000;

const TEMPLATE_STATUS_STYLE: Record<string, { color: string; bg: string }> = {
  APPROVED: { color: "#0a7a3b", bg: "#dbf2e3" },
  REJECTED: { color: "#a4252e", bg: "#fbe2e2" },
  PENDING: { color: "#7a5a00", bg: "#fbecc5" },
  PAUSED: { color: "#7a5a00", bg: "#fbecc5" },
  DISABLED: { color: "#5a5a5a", bg: "#e3e3e3" },
};

const MSG_STATUS_PALETTE: Record<
  string,
  { color: string; bg: string; label: string }
> = {
  queued: { color: "#7a5a00", bg: "#fbecc5", label: "En cola" },
  sent: { color: "#5a5a5a", bg: "#e3e3e3", label: "Enviado" },
  delivered: { color: "#1f6abf", bg: "#dbe7f7", label: "Entregado" },
  read: { color: "#0a7a3b", bg: "#dbf2e3", label: "Leído" },
  failed: { color: "#a4252e", bg: "#fbe2e2", label: "Falló" },
};

export function WhatsappAdminVisor({ tenantId }: { tenantId: string }) {
  const [status, setStatus] = useState<WhatsappAdminStatusOut | null>(null);
  const [metrics, setMetrics] = useState<WhatsappMetricsOut | null>(null);
  const [messages, setMessages] = useState<WhatsappMessageItem[]>([]);
  const [templates, setTemplates] = useState<WhatsappTemplateItem[]>([]);
  const [eventMappings, setEventMappings] = useState<EventMappingOut[]>([]);
  const [loadingTpl, setLoadingTpl] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  const [showTest, setShowTest] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testBody, setTestBody] = useState(
    "Mensaje de prueba desde MiPueblo ✅",
  );
  const [testBusy, setTestBusy] = useState(false);

  // Modal "Agregar WABA ID"
  const [showWabaModal, setShowWabaModal] = useState(false);
  const [wabaIdInput, setWabaIdInput] = useState("");
  const [wabaBusy, setWabaBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, m, msgs] = await Promise.all([
        whatsapp.status(tenantId),
        whatsapp.metrics(tenantId),
        whatsapp.messages(tenantId, 100),
      ]);
      setStatus(s);
      setMetrics(m);
      setMessages(msgs);
    } catch (err) {
      console.warn("wa visor load:", err);
    }
  }, [tenantId]);

  const loadTemplates = useCallback(
    async (refresh = false) => {
      setLoadingTpl(true);
      try {
        const t = await whatsapp.templates(tenantId, refresh);
        setTemplates(t);
      } catch (err) {
        setTemplates([]);
        if (err instanceof ApiError && err.status !== 404) {
          console.warn("Error cargando plantillas:", err);
        }
      } finally {
        setLoadingTpl(false);
      }
    },
    [tenantId],
  );

  const loadEventMappings = useCallback(async () => {
    try {
      const m = await whatsapp.eventMappings(tenantId);
      setEventMappings(m);
    } catch (err) {
      console.warn("eventos:", err);
      setEventMappings([]);
    }
  }, [tenantId]);

  const onEventChange = async (
    template: WhatsappTemplateItem,
    new_event_key: string,
  ) => {
    try {
      const prev = eventMappings.find(
        (m) => m.template_name === template.name && m.language === template.language,
      );
      if (prev && (new_event_key === "" || prev.key !== new_event_key)) {
        await whatsapp.eventMappingDelete(tenantId, prev.key);
      }
      if (new_event_key) {
        await whatsapp.eventMappingPut(tenantId, new_event_key, {
          template_name: template.name,
          language: template.language,
        });
      }
      await loadEventMappings();
      setOk("Mapeo de evento guardado");
      setTimeout(() => setOk(null), 2500);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Error guardando mapeo");
    }
  };

  const saveWaba = async () => {
    setWabaBusy(true);
    setError(null);
    try {
      await whatsapp.patchWaba(tenantId, wabaIdInput.trim());
      setOk("WABA ID guardado. Recargando plantillas…");
      setShowWabaModal(false);
      setWabaIdInput("");
      await load();
      await loadTemplates(true);
      setTimeout(() => setOk(null), 3000);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Error guardando WABA ID");
    } finally {
      setWabaBusy(false);
    }
  };

  const sendTest = async () => {
    setTestBusy(true);
    setError(null);
    try {
      const res = await whatsapp.test(tenantId, {
        to: testTo.replace(/\D/g, ""),
        body: testBody,
      });
      if (res.ok) {
        setOk(res.message);
        setShowTest(false);
        setTimeout(() => setOk(null), 4000);
      } else {
        setError(`Envío falló: ${res.message}`);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Error de red");
    } finally {
      setTestBusy(false);
    }
  };

  useEffect(() => {
    void load();
    void loadTemplates(false);
    void loadEventMappings();
    const t = setInterval(() => void load(), REFRESH_MS);
    return () => clearInterval(t);
  }, [load, loadTemplates, loadEventMappings]);

  return (
    <>
      {error && <div className="alert err">{error}</div>}
      {ok && <div className="alert ok">{ok}</div>}

      {/* Métricas + acciones */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-inner">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              marginBottom: 16,
            }}
          >
            <h3 style={{ margin: 0 }}>Mensajes del mes</h3>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                type="button"
                className="logout-btn"
                onClick={() => setShowTest(true)}
                style={{ padding: "6px 12px", fontSize: 12 }}
              >
                Probar conexión
              </button>
              <button
                type="button"
                className="logout-btn"
                onClick={() => void loadTemplates(true)}
                style={{ padding: "6px 12px", fontSize: 12 }}
              >
                Sincronizar plantillas
              </button>
            </div>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: 12,
            }}
          >
            <Metric label="Enviados" value={metrics?.enviados ?? 0} color="#0a7a3b" />
            <Metric
              label="Conversaciones"
              value={metrics?.conversaciones ?? 0}
              color="var(--ink-2)"
            />
            <Metric label="Recibidos" value={metrics?.recibidos ?? 0} color="#1f6abf" />
            <Metric
              label="Fallidos"
              value={metrics?.fallidos ?? 0}
              color={metrics?.fallidos ? "#a4252e" : "var(--ink-3)"}
            />
          </div>
        </div>
      </div>

      {/* Plantillas */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-inner">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              marginBottom: 12,
            }}
          >
            <h3 style={{ margin: 0 }}>Plantillas aprobadas en Meta</h3>
            <span style={{ fontSize: 11, color: "var(--ink-3)" }}>
              {templates.length} en total
            </span>
          </div>
          {loadingTpl ? (
            <p style={{ color: "var(--ink-3)" }}>Cargando plantillas…</p>
          ) : templates.length === 0 && status && !status.waba_id ? (
            <div
              style={{
                background: "#fbecc5",
                border: "1px solid #d8b85a",
                borderRadius: 8,
                padding: "16px 18px",
                fontSize: 13,
                color: "#5a4500",
              }}
            >
              <strong style={{ fontSize: 14 }}>
                ℹ️ Solo falta un dato para listar tus plantillas
              </strong>
              <p style={{ margin: "8px 0 12px 0", lineHeight: 1.6 }}>
                Tu canal WhatsApp <strong>funciona perfecto</strong> (Activo
                arriba). Pero para que este panel pueda traer la <em>lista</em>{" "}
                de plantillas aprobadas en Meta, necesita el{" "}
                <strong>WhatsApp Business Account ID</strong> (WABA ID).
              </p>
              <button
                type="button"
                className="logout-btn"
                onClick={() => setShowWabaModal(true)}
                style={{
                  background: "#5a4500",
                  color: "#fff",
                  borderColor: "#5a4500",
                  padding: "8px 16px",
                  fontWeight: 600,
                }}
              >
                Agregar WABA ID →
              </button>
              <details style={{ marginTop: 12 }}>
                <summary
                  style={{
                    cursor: "pointer",
                    padding: "4px 0",
                    color: "#5a4500",
                  }}
                >
                  ¿Dónde encuentro el WABA ID?
                </summary>
                <ol
                  style={{
                    margin: "8px 0 0 18px",
                    padding: 0,
                    lineHeight: 1.7,
                  }}
                >
                  <li>
                    Ve a{" "}
                    <a
                      href="https://developers.facebook.com/apps"
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: "#5a4500", textDecoration: "underline" }}
                    >
                      developers.facebook.com/apps
                    </a>{" "}
                    y abre tu app.
                  </li>
                  <li>
                    Menú lateral → <strong>WhatsApp</strong> →{" "}
                    <strong>API Setup</strong>.
                  </li>
                  <li>
                    Arriba verás "Phone number ID" (el que ya tienes) y, al
                    lado, "<strong>WhatsApp Business Account ID</strong>". Ese
                    número (15-16 dígitos) es el WABA ID. Cópialo.
                  </li>
                  <li>
                    Vuelve aquí → "Agregar WABA ID" → pégalo → Guardar.
                  </li>
                </ol>
              </details>
            </div>
          ) : templates.length === 0 ? (
            <p style={{ color: "var(--ink-3)", fontSize: 13 }}>
              No hay plantillas aprobadas en Meta para este WABA ID. Si acabas
              de configurarlo, espera unos minutos y usa <em>Sincronizar
              plantillas</em>. Si crees que debería haber alguna, verifica
              que el token tenga el permiso{" "}
              <code style={{ fontFamily: "var(--font-mono)" }}>
                whatsapp_business_management
              </code>
              .
            </p>
          ) : (
            <PlantillasTable
              templates={templates}
              eventMappings={eventMappings}
              onEventChange={onEventChange}
            />
          )}
        </div>
      </div>

      {/* Últimos mensajes */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-inner">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              marginBottom: 12,
            }}
          >
            <h3 style={{ margin: 0 }}>Últimos mensajes</h3>
            <span style={{ fontSize: 11, color: "var(--ink-3)" }}>
              {messages.length}
            </span>
          </div>
          {messages.length === 0 ? (
            <p style={{ color: "var(--ink-3)", fontSize: 13 }}>
              Aún no hay mensajes WhatsApp para este tenant.
            </p>
          ) : (
            <MensajesTable messages={messages} />
          )}
        </div>
      </div>

      {/* Modal probar conexión */}
      {showTest && (
        <div
          onClick={() => !testBusy && setShowTest(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "grid",
            placeItems: "center",
            zIndex: 100,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="card"
            style={{ maxWidth: 480, width: "92%" }}
          >
            <div className="card-inner">
              <h3 style={{ marginTop: 0 }}>Probar conexión WhatsApp</h3>
              <p style={{ color: "var(--ink-3)", fontSize: 13 }}>
                Envía un mensaje de prueba al número indicado (E.164 sin +).
              </p>
              <label className="label">
                Número destino
                <input
                  type="text"
                  value={testTo}
                  onChange={(e) => setTestTo(e.target.value)}
                  placeholder="573115275045"
                  className="input"
                />
              </label>
              <label className="label" style={{ marginTop: 12 }}>
                Mensaje
                <textarea
                  value={testBody}
                  onChange={(e) => setTestBody(e.target.value)}
                  className="input"
                  rows={3}
                  style={{ resize: "vertical" }}
                />
              </label>
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  justifyContent: "flex-end",
                  marginTop: 16,
                }}
              >
                <button
                  type="button"
                  className="logout-btn"
                  onClick={() => setShowTest(false)}
                  disabled={testBusy}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="logout-btn"
                  onClick={() => void sendTest()}
                  disabled={testBusy || testTo.replace(/\D/g, "").length < 8}
                >
                  {testBusy ? "Enviando…" : "Enviar"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Agregar WABA ID */}
      {showWabaModal && (
        <div
          onClick={() => !wabaBusy && setShowWabaModal(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "grid",
            placeItems: "center",
            zIndex: 100,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="card"
            style={{ maxWidth: 520, width: "92%" }}
          >
            <div className="card-inner">
              <h3 style={{ marginTop: 0 }}>Agregar WABA ID</h3>
              <p style={{ color: "var(--ink-3)", fontSize: 13, lineHeight: 1.5 }}>
                Pega aquí el <strong>WhatsApp Business Account ID</strong> que
                obtuviste de{" "}
                <a
                  href="https://developers.facebook.com/apps"
                  target="_blank"
                  rel="noreferrer"
                >
                  developers.facebook.com
                </a>{" "}
                → tu app → WhatsApp → API Setup. Es un número de 15-16
                dígitos. Se guarda sin tocar el resto de tus credenciales.
              </p>
              <label className="label" style={{ marginTop: 12 }}>
                WABA ID
                <input
                  type="text"
                  value={wabaIdInput}
                  onChange={(e) => setWabaIdInput(e.target.value)}
                  placeholder="123456789012345"
                  className="input"
                  style={{ fontFamily: "var(--font-mono)" }}
                  autoFocus
                />
              </label>
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  justifyContent: "flex-end",
                  marginTop: 18,
                }}
              >
                <button
                  type="button"
                  className="logout-btn"
                  onClick={() => setShowWabaModal(false)}
                  disabled={wabaBusy}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="logout-btn"
                  onClick={() => void saveWaba()}
                  disabled={wabaBusy || wabaIdInput.replace(/\D/g, "").length < 10}
                  style={{
                    background: "var(--ink)",
                    color: "#fff",
                    borderColor: "var(--ink)",
                  }}
                >
                  {wabaBusy ? "Guardando…" : "Guardar"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Metric({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color: string;
}) {
  return (
    <div style={{ textAlign: "center" }}>
      <div
        style={{
          fontSize: 32,
          fontWeight: 800,
          color,
          fontFamily: "var(--font-mono)",
          lineHeight: 1,
        }}
      >
        {value.toLocaleString("es-CO")}
      </div>
      <div
        style={{
          fontSize: 10,
          color: "var(--ink-3)",
          textTransform: "uppercase",
          letterSpacing: 0.5,
          marginTop: 4,
          fontFamily: "var(--font-mono)",
        }}
      >
        {label}
      </div>
    </div>
  );
}

function PlantillasTable({
  templates,
  eventMappings,
  onEventChange,
}: {
  templates: WhatsappTemplateItem[];
  eventMappings: EventMappingOut[];
  onEventChange: (t: WhatsappTemplateItem, eventKey: string) => Promise<void>;
}) {
  // Aplanamos los eventos en una lista ordenada por grupo (Citas →
  // PQRSDF → Soporte) y usamos `hint` del CustomSelect para mostrar el
  // grupo a la derecha del label. Como CustomSelect no soporta
  // optgroups, esta es la mejor forma de mantener la agrupación visual
  // sin perder consistencia con el resto del panel.
  const groupedOptions: ReadonlyArray<CustomSelectOption<string>> = [
    { value: "", label: "— sin asignar —" },
    ...eventMappings.map((e) => ({
      value: e.key,
      label: e.label,
      hint: e.group,
    })),
  ];
  const eventForTemplate = (t: WhatsappTemplateItem): string => {
    const m = eventMappings.find(
      (em) => em.template_name === t.name && em.language === t.language,
    );
    return m?.key ?? "";
  };

  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
      <thead>
        <tr
          style={{
            textAlign: "left",
            color: "var(--ink-3)",
            fontSize: 11,
            textTransform: "uppercase",
            letterSpacing: 0.5,
            fontFamily: "var(--font-mono)",
          }}
        >
          <th style={{ padding: "8px 0" }}>Nombre</th>
          <th>Categoría</th>
          <th>Idioma</th>
          <th>Estado</th>
          <th style={{ minWidth: 260 }}>Evento MiPueblo</th>
        </tr>
      </thead>
      <tbody>
        {templates.map((t) => {
          const current = eventForTemplate(t);
          const palette = TEMPLATE_STATUS_STYLE[t.status.toUpperCase()] ?? {
            color: "var(--ink-2)",
            bg: "var(--hairline)",
          };
          return (
            <tr
              key={`${t.name}-${t.language}`}
              style={{ borderTop: "1px solid var(--hairline)" }}
            >
              <td style={{ padding: "10px 0", fontFamily: "var(--font-mono)" }}>
                {t.name}
              </td>
              <td style={{ color: "var(--ink-2)" }}>
                {(t.category ?? "—").toUpperCase()}
              </td>
              <td style={{ color: "var(--ink-2)" }}>{t.language}</td>
              <td>
                <span
                  style={{
                    fontSize: 10,
                    fontFamily: "var(--font-mono)",
                    letterSpacing: 0.5,
                    textTransform: "uppercase",
                    padding: "3px 8px",
                    borderRadius: 999,
                    background: palette.bg,
                    color: palette.color,
                  }}
                >
                  {t.status}
                </span>
              </td>
              <td>
                <CustomSelect<string>
                  value={current}
                  onChange={(v) => void onEventChange(t, v)}
                  options={groupedOptions}
                  placeholder="— sin asignar —"
                  disabled={t.status.toUpperCase() !== "APPROVED"}
                  size="sm"
                  aria-label="Evento MiPueblo asociado a esta plantilla"
                />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function MensajesTable({ messages }: { messages: WhatsappMessageItem[] }) {
  const [q, setQ] = useState("");
  const [dir, setDir] = useState<"all" | "in" | "out">("all");

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return messages.filter((m) => {
      if (dir !== "all" && m.direction !== dir) return false;
      if (!needle) return true;
      const hay = `${m.numero ?? ""} ${m.contenido ?? ""} ${m.template_name ?? ""}`.toLowerCase();
      return hay.includes(needle);
    });
  }, [messages, q, dir]);

  const chip = (val: "all" | "in" | "out", label: string) => (
    <button
      type="button"
      onClick={() => setDir(val)}
      style={{
        fontSize: 11,
        fontFamily: "var(--font-mono)",
        textTransform: "uppercase",
        letterSpacing: 0.3,
        padding: "4px 10px",
        borderRadius: 999,
        border: "1px solid var(--hairline-strong)",
        background: dir === val ? "var(--ink)" : "transparent",
        color: dir === val ? "var(--paper)" : "var(--ink-2)",
        cursor: "pointer",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </button>
  );

  const thBase: CSSProperties = {
    position: "sticky",
    top: 0,
    background: "var(--paper)",
    zIndex: 1,
    padding: "8px 0",
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          marginBottom: 10,
          flexWrap: "wrap",
        }}
      >
        <input
          className="input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Filtrar por número o contenido…"
          style={{ flex: "1 1 200px", minWidth: 180 }}
        />
        <div style={{ display: "flex", gap: 6 }}>
          {chip("all", "Todos")}
          {chip("in", "← Recibidos")}
          {chip("out", "Enviados →")}
        </div>
      </div>

      <div style={{ maxHeight: 380, overflowY: "auto", overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr
              style={{
                textAlign: "left",
                color: "var(--ink-3)",
                fontSize: 11,
                textTransform: "uppercase",
                letterSpacing: 0.5,
                fontFamily: "var(--font-mono)",
              }}
            >
              <th style={thBase}>Fecha</th>
              <th style={{ ...thBase, width: 40 }}>Dir</th>
              <th style={thBase}>Número</th>
              <th style={thBase}>Contenido</th>
              <th style={{ ...thBase, width: 90 }}>Estado</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((m, i) => {
            const palette = MSG_STATUS_PALETTE[m.status] ?? {
              color: "var(--ink-2)",
              bg: "var(--hairline)",
              label: m.status,
            };
            const label =
              m.status === "delivered" && m.direction === "in"
                ? "Recibido"
                : palette.label;
            return (
              <tr
                key={i}
                style={{ borderTop: "1px solid var(--hairline)" }}
                title={m.error_msg ?? undefined}
              >
                <td
                  style={{
                    padding: "8px 0",
                    fontFamily: "var(--font-mono)",
                    whiteSpace: "nowrap",
                    color: "var(--ink-2)",
                    fontSize: 12,
                  }}
                >
                  {new Date(m.fecha).toLocaleString("es-CO", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </td>
                <td
                  style={{
                    color: m.direction === "in" ? "#1f6abf" : "#0a7a3b",
                    fontWeight: 700,
                    textAlign: "center",
                  }}
                >
                  {m.direction === "in" ? "←" : "→"}
                </td>
                <td
                  style={{
                    fontFamily: "var(--font-mono)",
                    color: "var(--ink-2)",
                    whiteSpace: "nowrap",
                    fontSize: 12,
                  }}
                >
                  {m.numero ?? "—"}
                </td>
                <td
                  style={{
                    color: "var(--ink)",
                    maxWidth: 500,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {m.template_name && (
                    <span style={{ color: "var(--ink-3)", fontSize: 11 }}>
                      [Plantilla:{" "}
                      <span style={{ fontFamily: "var(--font-mono)" }}>
                        {m.template_name}
                      </span>
                      ]{" "}
                    </span>
                  )}
                  {m.contenido || (m.kind === "media" ? "📎 Multimedia" : "—")}
                </td>
                <td>
                  <span
                    style={{
                      fontSize: 10,
                      fontFamily: "var(--font-mono)",
                      letterSpacing: 0.3,
                      padding: "2px 6px",
                      borderRadius: 999,
                      background: palette.bg,
                      color: palette.color,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {label}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
        {filtered.length === 0 && (
          <p
            style={{
              color: "var(--ink-3)",
              fontSize: 13,
              padding: "16px 4px",
              margin: 0,
            }}
          >
            Ningún mensaje coincide con el filtro.
          </p>
        )}
      </div>
      {(q !== "" || dir !== "all") && (
        <p
          style={{
            color: "var(--ink-3)",
            fontSize: 11,
            marginTop: 8,
            marginBottom: 0,
          }}
        >
          Mostrando {filtered.length} de {messages.length}
        </p>
      )}
    </div>
  );
}
